// GroupedEntriesView.js - FIXED: Handles undefined entries
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import TimeEntryCard from './TimeEntryCard';

const GroupedEntriesView = ({ 
  entries = [], // DEFAULT VALUE
  groupBy, 
  clients = [], // DEFAULT VALUE
  projects = [], // DEFAULT VALUE
  teamMembers = [], // DEFAULT VALUE
  selectedEntries = [], // DEFAULT VALUE
  onToggleSelection,
  onEdit,
  onDuplicate,
  onDelete,
  onView,
  onStatusUpdate,
  onSplit,
  onRevertSplit,
  onAddComment,
  onUpdateTags,
  userRole
}) => {
  // HOOKS MUST BE CALLED FIRST - before any conditional returns
  const [expanded, setExpanded] = useState({});
  
  const getGroupedEntries = () => {
    const groups = {};
    
    // SAFETY CHECK: Handle undefined or non-array entries
    if (!entries || !Array.isArray(entries)) {
      console.warn('GroupedEntriesView: entries prop is not an array', entries);
      return [];
    }
    
    entries.forEach(entry => {
      let groupKey, groupLabel;
      
      switch (groupBy) {
        case 'user': {
          groupKey = entry.user_id || 'unknown';
          groupLabel = entry.user_name || 'Unknown User';
          break;
        }
        case 'role': {
          groupKey = entry.bill_rate_role || 'unassigned';
          groupLabel = entry.bill_rate_role 
            ? entry.bill_rate_role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : 'Unassigned';
          break;
        }
        case 'client': {
          groupKey = entry.client_id || 'no_client';
          groupLabel = entry.client_name || 'No Client';
          break;
        }
        case 'project': {
          groupKey = entry.project_id || 'no_project';
          groupLabel = entry.project_name || 'No Project';
          break;
        }
        default: {
          groupKey = 'all';
          groupLabel = 'All Entries';
        }
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = { 
          label: groupLabel, 
          entries: [], 
          totalHours: 0 
        };
      }
      groups[groupKey].entries.push(entry);
      groups[groupKey].totalHours += entry.duration_hours || 0;
    });
    
    return Object.entries(groups).sort((a, b) => b[1].totalHours - a[1].totalHours);
  };

  const groupedData = getGroupedEntries();

  useEffect(() => {
    const next = {};
    groupedData.forEach(([key]) => { next[key] = false; });
    setExpanded(next);
  }, [entries.length, groupBy]); // Use entries.length instead of entries

  const toggleOne = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  
  const setAll = (open) => {
    const next = {};
    groupedData.forEach(([key]) => { next[key] = open; });
    setExpanded(next);
  };

  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  // NOW we can return early if needed - AFTER all hooks are called
  if (!entries || !Array.isArray(entries) || entries.length === 0) {
    return (
      <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200">
        <p className="text-slate-600">No entries to display</p>
      </div>
    );
  }

  if (groupedData.length === 0) {
    return (
      <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200">
        <p className="text-slate-600">No entries found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick expand/collapse all */}
      <div className="flex justify-end gap-2">
        <button 
          type="button"
          onClick={() => setAll(true)} 
          className="text-sm px-3 py-1 rounded bg-slate-100 hover:bg-slate-200"
        >
          Expand all
        </button>
        <button 
          type="button"
          onClick={() => setAll(false)} 
          className="text-sm px-3 py-1 rounded bg-slate-100 hover:bg-slate-200"
        >
          Collapse all
        </button>
      </div>

      {groupedData.map(([groupKey, groupData]) => (
        <div key={groupKey} className="bg-slate-50 rounded-xl p-4">
          {/* Group Header with toggle */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-lg font-semibold text-blue-600">
                  {groupData.label.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{groupData.label}</h3>
                <p className="text-sm text-slate-600">
                  {groupData.entries.length} {groupData.entries.length === 1 ? 'entry' : 'entries'} • {formatTime(groupData.totalHours)} total
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => toggleOne(groupKey)}
              className="inline-flex items-center text-sm px-3 py-1.5 rounded bg-white border border-slate-200 hover:bg-slate-50"
              aria-expanded={expanded[groupKey] ? 'true' : 'false'}
            >
              {expanded[groupKey] ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" /> Collapse
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" /> Expand
                </>
              )}
            </button>
          </div>

          {/* Group entries (render only when expanded) */}
          {expanded[groupKey] && (
            <div className="space-y-4">
              {groupData.entries.map(entry => (
                <div key={entry.id} className="relative">
                  <div className="absolute left-2 top-4 z-10">
                    <input
                      type="checkbox"
                      checked={selectedEntries?.includes(entry.id) || false}
                      onChange={() => onToggleSelection?.(entry.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-8">
                    <TimeEntryCard
                      entry={{ 
                        ...entry, 
                        comments: entry.comments || [], 
                        tags: entry.tags || [], 
                        priority: entry.priority || 'medium' 
                      }}
                      clients={clients}
                      projects={projects}
                      onEdit={onEdit}
                      onDuplicate={onDuplicate}
                      onDelete={onDelete}
                      onView={onView}
                      onStatusUpdate={onStatusUpdate}
                      onSplit={onSplit}
                      onRevertSplit={onRevertSplit}
                      onAddComment={onAddComment}
                      onUpdateTags={onUpdateTags}
                      userRole={userRole}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Custom equality check: Only re-render if entries, groupBy, or selectedEntries.length change
const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.entries?.length === nextProps.entries?.length &&
    prevProps.groupBy === nextProps.groupBy &&
    prevProps.selectedEntries?.length === nextProps.selectedEntries?.length &&
    prevProps.clients?.length === nextProps.clients?.length &&
    prevProps.projects?.length === nextProps.projects?.length &&
    prevProps.teamMembers?.length === nextProps.teamMembers?.length
  );
};

export default React.memo(GroupedEntriesView, areEqual);